import { TextField, Button } from "@mui/material";
import React, { useState, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import { 
  AiFillDelete, 
  AiFillEdit, 
  AiOutlinePlus, 
  AiOutlineDownload, 
  AiOutlineCheckCircle, 
  AiOutlineCloseCircle, 
  AiOutlineCloudUpload,
  AiOutlineSearch,
  AiOutlineClear,
  AiOutlinePlayCircle,
  AiOutlineSync
} from "react-icons/ai";
import { 
  FaFileExcel, 
  FaImages, 
  FaRegFileImage, 
  FaPlay, 
  FaTimes, 
  FaInfoCircle,
  FaArrowRight
} from "react-icons/fa";
import { server } from "../common";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useSelectAccess } from "../store/stateFunctions";
import * as XLSX from "xlsx";
import JSZip from "jszip";

const CATEGORIES = {
  "Types of Training Programs": [
    "Physical Training",
    "Written Exam Preparation",
    "Medical & Psychological Training"
  ],
  "Campus Facilities": [
    "Running Track & Ground",
    "Fully Equipped Digital Classrooms",
    "Computer Lab",
    "Library & Study Hall",
    "Mess / Diet Facility",
    "First Aid",
    "24-hour CCTV surveillance and strict discipline",
    "Distinctive accommodation facilities"
  ]
};

const initialValues = {
  title: "",
  image: null,
  type: "photo",
  category: "Types of Training Programs",
  subcategory: "Physical Training",
  video_url: "",
  description: "",
  sequence: 0,
};

const swalalert = withReactContent(Swal);

// Utility helper to normalize strings for robust fuzzy/case-insensitive matching
const normalizeString = (str) => {
  if (!str) return "";
  return str.toLowerCase().replace(/[^a-z0-9]/g, "");
};

// Generates dynamic, high-quality, lightweight sample images for ZIP imports
const generatePlaceholderImage = (text, bgHex) => {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = 600;
    canvas.height = 400;
    const ctx = canvas.getContext("2d");

    // Background filling
    ctx.fillStyle = bgHex;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Subtle framing border
    ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
    ctx.lineWidth = 12;
    ctx.strokeRect(10, 10, canvas.width - 20, canvas.height - 20);

    // Primary Text
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 32px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, canvas.width / 2, canvas.height / 2 - 20);

    // Professional branding subtext
    ctx.fillStyle = "rgba(255, 255, 255, 0.65)";
    ctx.font = "italic 16px sans-serif";
    ctx.fillText("Venkateshwara Farmers Welfare Academy", canvas.width / 2, canvas.height / 2 + 35);

    // Export to Blob
    canvas.toBlob((blob) => {
      resolve(blob);
    }, "image/jpeg", 0.85);
  });
};

const AddGallery = () => {
  const user = useSelectAccess("Super Admin") || {};
  const token = user.authToken || localStorage.getItem("superadminToken");

  const [activeTab, setActiveTab] = useState("manage"); // "manage" or "bulk"
  const [show, setShow] = useState(false);
  const [data, setData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Search and Filter States for Previous Gallery Table
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterSubcategory, setFilterSubcategory] = useState("all");

  // Bulk Upload States
  const [excelFile, setExcelFile] = useState(null);
  const [excelData, setExcelData] = useState([]); // Array of parsed rows
  const [selectedImages, setSelectedImages] = useState({}); // filename (lowercase) -> File object
  const [uploading, setUploading] = useState(false);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(-1);
  const [successCount, setSuccessCount] = useState(0);
  const [failCount, setFailCount] = useState(0);
  const [uploadLogs, setUploadLogs] = useState([]); // Array of objects { time, message, type: 'success' | 'error' | 'info' }
  
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const consoleEndRef = useRef(null);

  useEffect(() => {
    getData();
  }, []);

  // Auto-scroll the log console to bottom when new logs are added
  useEffect(() => {
    if (consoleEndRef.current) {
      consoleEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [uploadLogs]);

  const handleClose = () => {
    setShow(false);
    setIsEditMode(false);
    setSelectedData(null);
    formik.resetForm();
  };

  const handleShow = () => {
    setIsEditMode(false);
    setSelectedData(null);
    formik.resetForm();
    setShow(true);
  };

  const handleShowEdit = (item) => {
    setSelectedData(item);
    setIsEditMode(true);
    formik.setValues({
      title: item.title,
      image: null,
      type: item.type || "photo",
      category: item.category || "Types of Training Programs",
      subcategory: item.subcategory || "Physical Training",
      video_url: item.video_url || "",
      description: item.description || "",
      sequence: item.sequence || 0,
    });
    setShow(true);
  };

  // Get all gallery items from database
  const getData = () => {
    server
      .get("/gallery/getallgallery")
      .then((response) => {
        if (response.status === 200) {
          setData(response.data);
        }
      })
      .catch((error) => {
        toast.error("Failed to load gallery items");
      });
  };

  // Delete individual item
  const deleteGallery = (item) => {
    swalalert
      .fire({
        title: "Delete Confirmation!",
        text: `Are you sure you want to delete "${item.title}" ?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Delete",
        confirmButtonColor: "#ff671f",
        cancelButtonText: "Cancel",
      })
      .then((res) => {
        if (res.isConfirmed) {
          server
            .delete(`/gallery/deletegallery/${item.id}`, {
              headers: { Authorization: token },
            })
            .then(() => {
              toast.success("Gallery item deleted successfully");
              getData();
            })
            .catch(() => toast.error("Failed to delete item"));
        }
      });
  };

  // Update item
  const handleUpdate = (values) => {
    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("type", values.type);
    formData.append("category", values.category);
    formData.append("subcategory", values.subcategory);
    formData.append("sequence", values.sequence || 0);

    if (values.type === "photo") {
      if (values.image) {
        formData.append("image", values.image);
      }
    } else {
      formData.append("video_url", values.video_url || selectedData.video_url);
      formData.append("description", values.description || "");
    }

    server
      .put(`/gallery/updategallery/${selectedData.id}`, formData, {
        headers: {
          Authorization: token,
        },
      })
      .then(() => {
        toast.success("Gallery item updated successfully");
        getData();
        handleClose();
      })
      .catch(() => toast.error("Failed to update item"));
  };

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      title: Yup.string().required("Enter a title"),
      category: Yup.string().required("Category is required"),
      subcategory: Yup.string().required("Subcategory is required"),
      sequence: Yup.number().min(0, "Sequence must be 0 or greater"),
    }),
    onSubmit: (values) => {
      if (isEditMode) {
        handleUpdate(values);
      } else {
        handleAdd(values);
      }
    },
  });

  // Add individual item
  const handleAdd = (values) => {
    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("type", values.type);
    formData.append("category", values.category);
    formData.append("subcategory", values.subcategory);
    formData.append("sequence", values.sequence || 0);

    if (values.type === "photo") {
      if (!values.image) {
        toast.error("Image file is required");
        return;
      }
      formData.append("image", values.image);
    } else {
      if (!values.video_url) {
        toast.error("Video URL is required");
        return;
      }
      formData.append("video_url", values.video_url);
      formData.append("description", values.description || "");
    }

    server
      .post("/gallery/addgallery", formData, {
        headers: { 
          Authorization: token,
        },
      })
      .then(() => {
        toast.success("Gallery item added successfully");
        getData();
        handleClose();
      })
      .catch(() => toast.error("Failed to add item"));
  };

  // --- Bulk Import Core Logic ---

  // 1. Download formatted ZIP Starter Template dynamically (Excel + Mapped Sample Pictures)
  const downloadTemplate = async () => {
    try {
      toast.info("Generating starter package ZIP... Please wait.");
      
      const templateData = [
        {
          "Title": "Physical Training Session Outdoor",
          "Type": "photo",
          "Category": "Types of Training Programs",
          "Subcategory": "Physical Training",
          "Image Name": "outdoor_training.jpg",
          "Video URL": "",
          "Description": "Cadets performing early morning outdoor endurance training.",
          "Sequence": 1
        },
        {
          "Title": "Digital Classroom Overview",
          "Type": "photo",
          "Category": "Campus Facilities",
          "Subcategory": "Fully Equipped Digital Classrooms",
          "Image Name": "digital_classroom_1.png",
          "Video URL": "",
          "Description": "Smart board interactive session for competitive written exams.",
          "Sequence": 2
        },
        {
          "Title": "Academy Promo Video",
          "Type": "video",
          "Category": "Types of Training Programs",
          "Subcategory": "Written Exam Preparation",
          "Image Name": "",
          "Video URL": "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
          "Description": "A comprehensive video showcasing written exam batch coaching.",
          "Sequence": 3
        }
      ];

      const ws = XLSX.utils.json_to_sheet(templateData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Bulk Upload Format");

      // Column widths
      ws["!cols"] = [
        { wch: 32 }, // Title
        { wch: 10 }, // Type
        { wch: 30 }, // Category
        { wch: 32 }, // Subcategory
        { wch: 25 }, // Image Name
        { wch: 45 }, // Video URL
        { wch: 45 }, // Description
        { wch: 12 }  // Sequence
      ];

      // Convert workbook to array buffer in memory
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

      // Generate dynamic sample placeholder image files to match Excel imageName fields
      const img1Blob = await generatePlaceholderImage("Physical Training", "#ff671f");
      const img2Blob = await generatePlaceholderImage("Smart Digital Classroom", "#00b894");

      // Create ZIP package
      const zip = new JSZip();
      zip.file("Gallery_Bulk_Upload_Template.xlsx", excelBuffer);
      zip.file("outdoor_training.jpg", img1Blob);
      zip.file("digital_classroom_1.png", img2Blob);

      // Compile ZIP in the client
      const zipBlob = await zip.generateAsync({ type: "blob" });

      // Trigger automatic file download
      const url = window.URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Gallery_Bulk_Import_Package.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success("Gallery Import Package ZIP downloaded successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate zip package template.");
    }
  };

  // 2. Parse Excel File
  const handleExcelUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setExcelFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const dataBytes = new Uint8Array(e.target.result);
        const workbook = XLSX.read(dataBytes, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rawJson = XLSX.utils.sheet_to_json(worksheet);

        if (rawJson.length === 0) {
          toast.warning("The Excel file seems to be empty.");
          return;
        }

        const parsedRows = rawJson.map((row, idx) => {
          const getVal = (possibleKeys) => {
            const cleanSearchKeys = possibleKeys.map(pk => pk.toLowerCase().replace(/[^a-z0-9]/g, ""));
            const foundKey = Object.keys(row).find(k => {
              // Strip hidden control characters, BOM, spaces, and hyphens
              const cleanKey = k.toLowerCase().replace(/[^a-z0-9]/g, "");
              return cleanSearchKeys.includes(cleanKey);
            });
            return foundKey && row[foundKey] !== undefined && row[foundKey] !== null
              ? String(row[foundKey]).trim()
              : "";
          };

          const title = getVal(["title", "gallery title"]);
          const typeVal = getVal(["type", "item type", "gallery type"]).toLowerCase().trim() || "photo";
          const categoryInput = getVal(["category", "gallery category"]);
          const subcategoryInput = getVal(["subcategory", "sub category", "gallery subcategory"]);
          const imageName = getVal(["image name", "imagename", "image", "file name", "filename"]);
          const videoUrl = getVal(["video url", "videourl", "video link", "video"]);
          const description = getVal(["description", "descr", "video description"]);
          const sequenceInput = getVal(["sequence", "order", "seq"]);

          let errors = [];
          if (!title) errors.push("Title is required");
          if (typeVal !== "photo" && typeVal !== "video") {
            errors.push("Type must be 'photo' or 'video'");
          }

          // Smart category resolution
          let matchedCategory = "";
          let matchedSubcategory = "";

          if (!categoryInput) {
            errors.push("Category is required");
          } else {
            const targetCatNorm = normalizeString(categoryInput);
            const foundCat = Object.keys(CATEGORIES).find(
              cat => normalizeString(cat) === targetCatNorm
            );

            if (foundCat) {
              matchedCategory = foundCat;
              
              if (!subcategoryInput) {
                errors.push("Subcategory is required");
              } else {
                const targetSubNorm = normalizeString(subcategoryInput);
                const foundSub = CATEGORIES[foundCat].find(
                  sub => normalizeString(sub) === targetSubNorm
                );

                if (foundSub) {
                  matchedSubcategory = foundSub;
                } else {
                  errors.push(`Subcategory matches none in '${foundCat}'.`);
                }
              }
            } else {
              errors.push("Invalid Category. Must be training or campus facilities.");
            }
          }

          if (typeVal === "photo" && !imageName) {
            errors.push("Image name is required for photo items");
          }
          if (typeVal === "video" && !videoUrl) {
            errors.push("Video URL is required for video items");
          }

          return {
            id: idx + 1,
            title,
            type: typeVal === "video" ? "video" : "photo",
            category: matchedCategory || categoryInput,
            subcategory: matchedSubcategory || subcategoryInput,
            imageName,
            videoUrl,
            description,
            sequence: parseInt(sequenceInput) || 0,
            errors,
            status: "pending", // pending, uploading, success, failed
            errorMessage: ""
          };
        });

        setExcelData(parsedRows);
        toast.success(`Excel file parsed! Loaded ${parsedRows.length} items.`);
      } catch (err) {
        console.error(err);
        toast.error("Error parsing the Excel file. Please double-check formatting.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // 3. Multi-image selection (supports both files and zip archives)
  const handleImageSelection = async (event) => {
    const files = Array.from(event.target.files);
    const newImages = {};
    
    // Check if a zip archive was selected
    const zipFile = files.find(file => file.name.toLowerCase().endsWith(".zip"));

    if (zipFile) {
      toast.info("Extracting image ZIP file... Please wait.");
      try {
        const zip = new JSZip();
        const loadedZip = await zip.loadAsync(zipFile);
        
        const imagePromises = [];
        loadedZip.forEach((relativePath, fileEntry) => {
          // Check if it's an image file and not in Mac OS metadata directories
          if (!fileEntry.dir && !relativePath.startsWith("__MACOSX") && /\.(jpe?g|png|webp|gif|bmp)$/i.test(relativePath)) {
            // Extract the filename from the path
            const filename = relativePath.split("/").pop();
            
            const promise = fileEntry.async("blob").then(blob => {
              // Convert extracted blob to a standard File object
              const imageFile = new File([blob], filename, { type: blob.type || "image/jpeg" });
              newImages[filename.toLowerCase()] = imageFile;
            });
            imagePromises.push(promise);
          }
        });
        
        await Promise.all(imagePromises);
        
        if (Object.keys(newImages).length === 0) {
          toast.warning("No valid images found inside the ZIP archive.");
          return;
        }

        setSelectedImages(prev => ({ ...prev, ...newImages }));
        toast.success(`Successfully extracted and loaded ${Object.keys(newImages).length} images from ZIP archive!`);
      } catch (err) {
        console.error("ZIP Extraction Error:", err);
        toast.error("Failed to extract ZIP archive. Make sure it is a valid zip file.");
      }
    } else {
      // Normal multi-file selection
      files.forEach(file => {
        newImages[file.name.toLowerCase()] = file;
      });
      setSelectedImages(prev => ({ ...prev, ...newImages }));
      toast.success(`Loaded ${files.length} images to import pool!`);
    }
  };

  // Helper check status
  const isImageMatched = (imageName) => {
    if (!imageName) return false;
    return !!selectedImages[imageName.toLowerCase()];
  };

  const getMatchedFile = (imageName) => {
    if (!imageName) return null;
    return selectedImages[imageName.toLowerCase()] || null;
  };

  const removeRowFromImport = (id) => {
    setExcelData(prev => prev.filter(item => item.id !== id));
  };

  // 4. Reset Bulk Import Panel
  const clearBulkImport = () => {
    setExcelFile(null);
    setExcelData([]);
    setSelectedImages({});
    setSuccessCount(0);
    setFailCount(0);
    setUploadLogs([]);
    setUploading(false);
    setCurrentUploadIndex(-1);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  // 5. Start Sequential Bulk Upload Loop
  const startBulkUpload = async () => {
    const rowsToUpload = excelData.filter(row => {
      if (row.errors.length > 0) return false;
      if (row.type === "photo" && !isImageMatched(row.imageName)) return false;
      return true;
    });

    if (rowsToUpload.length === 0) {
      toast.warning("No valid and matched rows are ready to be uploaded!");
      return;
    }

    setUploading(true);
    setSuccessCount(0);
    setFailCount(0);
    setUploadLogs([
      {
        time: new Date().toLocaleTimeString(),
        message: `Starting bulk upload for ${rowsToUpload.length} items...`,
        type: "info"
      }
    ]);

    const updatedData = [...excelData];

    for (let i = 0; i < rowsToUpload.length; i++) {
      const row = rowsToUpload[i];
      const origIdx = excelData.findIndex(item => item.id === row.id);
      
      if (origIdx === -1) continue;

      // Mark row as uploading
      updatedData[origIdx] = { ...updatedData[origIdx], status: "uploading" };
      setExcelData([...updatedData]);
      setCurrentUploadIndex(origIdx);

      const formData = new FormData();
      formData.append("title", row.title);
      formData.append("type", row.type);
      formData.append("category", row.category);
      formData.append("subcategory", row.subcategory);
      formData.append("sequence", row.sequence || 0);

      if (row.type === "video") {
        formData.append("video_url", row.videoUrl);
        formData.append("description", row.description || "");
      } else {
        const imageFileObj = getMatchedFile(row.imageName);
        formData.append("image", imageFileObj);
      }

      try {
        const res = await server.post("/gallery/addgallery", formData, {
          headers: {
            Authorization: token,
          }
        });

        if (res.status === 200 || res.status === 201) {
          updatedData[origIdx] = { ...updatedData[origIdx], status: "success" };
          setSuccessCount(prev => prev + 1);
          setUploadLogs(prev => [
            ...prev,
            {
              time: new Date().toLocaleTimeString(),
              message: `[SUCCESS] Row #${row.id}: Uploaded "${row.title}" successfully.`,
              type: "success"
            }
          ]);
        } else {
          throw new Error(res.data?.error || "Invalid response from server");
        }
      } catch (err) {
        console.error(err);
        const errMsg = err.response?.data?.error || err.message || "Failed to connect";
        updatedData[origIdx] = { ...updatedData[origIdx], status: "failed", errorMessage: errMsg };
        setFailCount(prev => prev + 1);
        setUploadLogs(prev => [
          ...prev,
          {
            time: new Date().toLocaleTimeString(),
            message: `[ERROR] Row #${row.id} ("${row.title}"): ${errMsg}`,
            type: "error"
          }
        ]);
      }

      setExcelData([...updatedData]);
    }

    setUploading(false);
    setCurrentUploadIndex(-1);
    toast.success("Bulk Upload complete! See logs console for full status.");
    getData(); // Refresh main table
  };

  // Pre-calculated stats
  const totalDbItems = data.length;
  const photoCount = data.filter(d => d.type === "photo" || !d.type).length;
  const videoCount = data.filter(d => d.type === "video").length;

  // Filter & Search Logic
  const filteredDbData = data.filter(item => {
    const matchesSearch = item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.subcategory?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = filterCategory === "all" ? true : item.category === filterCategory;
    const matchesSubcategory = filterSubcategory === "all" ? true : item.subcategory === filterSubcategory;
    
    return matchesSearch && matchesCategory && matchesSubcategory;
  });

  return (
    <div className="container-fluid py-4 px-md-5" style={{ background: "#f8fafc", minHeight: "100vh" }}>
      {/* Brand Header */}
      <div 
        className="card border-0 shadow-sm mb-4 p-4 text-white" 
        style={{ 
          background: "linear-gradient(135deg, #ff671f 0%, #ff8e53 100%)",
          borderRadius: "16px"
        }}
      >
        <div className="row align-items-center">
          <div className="col-md-8">
            <h3 className="fw-bold mb-1">🖼️ Gallery Administration</h3>
            <p className="mb-0 opacity-85">Manage training program pictures, campus facility slides, and showcase videos seamlessly.</p>
          </div>
          <div className="col-md-4 text-md-end mt-3 mt-md-0">
            <div className="d-inline-flex gap-3 text-black bg-white bg-opacity-20 px-3 py-2 rounded-3 border border-white border-opacity-10">
              <div className="text-center">
                <span className="d-block fw-bold fs-4 line-height-1">{totalDbItems}</span>
                <small className="opacity-75" style={{ fontSize: "11px" }}>Total Items</small>
              </div>
              <div className="vr bg-white opacity-40"></div>
              <div className="text-center">
                <span className="d-block fw-bold fs-4 line-height-1">{photoCount}</span>
                <small className="opacity-75" style={{ fontSize: "11px" }}>Photos</small>
              </div>
              <div className="vr bg-white opacity-40"></div>
              <div className="text-center">
                <span className="d-block fw-bold fs-4 line-height-1">{videoCount}</span>
                <small className="opacity-75" style={{ fontSize: "11px" }}>Videos</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Switcher */}
      <div className="d-flex border-bottom mb-4 bg-white p-2 rounded-3 shadow-sm">
        <button
          className={`btn flex-grow-1 flex-md-grow-0 px-4 py-2 fw-bold d-flex align-items-center justify-content-center gap-2 rounded-2 transition-all ${
            activeTab === "manage" 
              ? "bg-primary-subtle text-success border border-success" 
              : "text-muted border-0"
          }`}
          style={{ 
            color: activeTab === "manage" ? "#ff671f" : "", 
            backgroundColor: activeTab === "manage" ? "rgba(255, 103, 31, 0.08)" : "transparent",
            borderColor: activeTab === "manage" ? "#ff671f" : "transparent"
          }}
          onClick={() => setActiveTab("manage")}
        >
          🎓 Manage Gallery
        </button>
        <button
          className={`btn flex-grow-1 flex-md-grow-0 px-4 py-2 fw-bold d-flex align-items-center justify-content-center gap-2 rounded-2 transition-all ms-2 ${
            activeTab === "bulk" 
              ? "bg-primary-subtle text-success border border-success" 
              : "text-muted border-0"
          }`}
          style={{ 
            color: activeTab === "bulk" ? "#ff671f" : "", 
            backgroundColor: activeTab === "bulk" ? "rgba(255, 103, 31, 0.08)" : "transparent",
            borderColor: activeTab === "bulk" ? "#ff671f" : "transparent"
          }}
          onClick={() => setActiveTab("bulk")}
        >
          📤 Bulk Import Panel (Excel)
        </button>
      </div>

      {/* TAB 1: MANAGE GALLERY */}
      {activeTab === "manage" && (
        <div className="card border-0 shadow-sm p-4" style={{ borderRadius: "12px" }}>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
            <h5 className="fw-bold mb-0 text-dark">Existing Gallery Records</h5>
            <button 
              className="btn text-white d-flex align-items-center justify-content-center gap-2" 
              style={{ backgroundColor: "#ff671f", borderRadius: "8px" }}
              onClick={handleShow}
            >
              <AiOutlinePlus size={18} /> Add Gallery Item
            </button>
          </div>

          {/* Filters Bar */}
          <div className="row g-3 mb-4 p-3 bg-light rounded-3">
            <div className="col-md-4">
              <label className="form-label fw-bold text-muted" style={{ fontSize: "12px" }}>Search by Title / Tag</label>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0 text-muted">
                  <AiOutlineSearch />
                </span>
                <input
                  type="text"
                  placeholder="Search gallery..."
                  className="form-control border-start-0"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-4">
              <label className="form-label fw-bold text-muted" style={{ fontSize: "12px" }}>Filter Category</label>
              <select
                className="form-select"
                value={filterCategory}
                onChange={(e) => {
                  setFilterCategory(e.target.value);
                  setFilterSubcategory("all");
                }}
              >
                <option value="all">All Categories</option>
                {Object.keys(CATEGORIES).map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label fw-bold text-muted" style={{ fontSize: "12px" }}>Filter Subcategory</label>
              <select
                className="form-select"
                value={filterSubcategory}
                disabled={filterCategory === "all"}
                onChange={(e) => setFilterSubcategory(e.target.value)}
              >
                <option value="all">All Subcategories</option>
                {filterCategory !== "all" && CATEGORIES[filterCategory].map(sub => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Table Container */}
          <div className="table-responsive">
            <table className="table table-hover border align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "60px" }} className="text-center">#</th>
                  <th>Category details</th>
                  <th>Item Title</th>
                  <th>Preview Type</th>
                  <th style={{ width: "80px" }} className="text-center">Order</th>
                  <th style={{ width: "120px" }} className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDbData.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-5">
                      <FaInfoCircle className="mb-2" size={24} />
                      <p className="mb-0">No gallery items found matching filters.</p>
                    </td>
                  </tr>
                ) : (
                  filteredDbData.map((item, index) => (
                    <tr key={item.id}>
                      <td className="text-center fw-semibold text-muted">{index + 1}</td>
                      <td>
                        <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                          {item.category === "Types of Training Programs" ? "🎓 " : "Mess / Study Area " && "🏫 "}
                          {item.category || "N/A"}
                        </div>
                        <div className="text-muted fw-semibold" style={{ fontSize: "11px" }}>
                          ↳ {item.subcategory || "N/A"}
                        </div>
                      </td>
                      <td className="fw-semibold text-dark">{item.title}</td>
                      <td>
                        {item.type === 'photo' || !item.type ? (
                          <div className="d-flex align-items-center gap-2">
                            <a href={item.image} target="_blank" rel="noreferrer">
                              <img src={item.image} alt="" className="rounded border shadow-xs" width="45" height="45" style={{ objectFit: "cover" }} />
                            </a>
                            <span className="badge bg-success-subtle text-success border border-success border-opacity-25" style={{ fontSize: "10px" }}>PHOTO</span>
                          </div>
                        ) : (
                          <div className="d-flex align-items-center gap-2">
                            <div className="d-flex align-items-center justify-content-center bg-danger-subtle rounded border border-danger border-opacity-25 text-danger" style={{ width: "45px", height: "45px" }}>
                              <AiOutlinePlayCircle size={20} />
                            </div>
                            <div style={{ maxWidth: "200px" }}>
                              <a href={item.video_url} target="_blank" rel="noreferrer" className="text-truncate d-block fw-semibold text-decoration-none text-primary" style={{ fontSize: "12px" }}>
                                {item.video_url}
                              </a>
                              <span className="badge bg-danger-subtle text-danger border border-danger border-opacity-25" style={{ fontSize: "10px" }}>VIDEO</span>
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="text-center font-monospace fw-bold">{item.sequence || 0}</td>
                      <td className="text-center">
                        <div className="d-inline-flex gap-2">
                          <button 
                            className="btn btn-outline-secondary btn-sm rounded-3 p-2"
                            onClick={() => handleShowEdit(item)}
                          >
                            <AiFillEdit size={16} />
                          </button>
                          <button 
                            className="btn btn-outline-danger btn-sm rounded-3 p-2"
                            onClick={() => deleteGallery(item)}
                          >
                            <AiFillDelete size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: BULK IMPORT WORKSPACE */}
      {activeTab === "bulk" && (
        <div className="row g-4">
          
          {/* Instructions Column */}
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm p-4 mb-4" style={{ borderRadius: "12px" }}>
              
              
              <button 
                type="button"
                className="btn btn-outline-dark w-100 py-2 d-flex align-items-center justify-content-center gap-2 fw-bold"
                onClick={downloadTemplate}
              >
                <AiOutlineDownload size={20} /> Download Starter ZIP Package (Excel + Images)
              </button>
            </div>
            
            {/* Upload Control Card */}
            <div className="card border-0 shadow-sm p-4" style={{ borderRadius: "12px" }}>
              <h5 className="fw-bold mb-4 text-dark d-flex align-items-center gap-2">
                <AiOutlineCloudUpload className="text-primary" /> Setup Upload Files
              </h5>

              {/* Excel input */}
              <div className="mb-4">
                <label className="form-label fw-bold text-secondary mb-2">Step 1: Upload Excel spreadsheet</label>
                <div 
                  className="p-3 border border-2 border-dashed rounded-3 text-center cursor-pointer hover-bg-light transition-all position-relative"
                  style={{ borderColor: excelFile ? "#2e7d32" : "#cbd5e1" }}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".xlsx, .xls, .csv"
                    className="position-absolute w-100 h-100 top-0 start-0 opacity-0 cursor-pointer"
                    onChange={handleExcelUpload}
                    disabled={uploading}
                  />
                  <FaFileExcel size={30} className={excelFile ? "text-success mb-2" : "text-muted mb-2"} />
                  {excelFile ? (
                    <div>
                      <div className="fw-bold text-success text-truncate">{excelFile.name}</div>
                      <small className="text-muted">{(excelFile.size / 1024).toFixed(1)} KB • Click to swap</small>
                    </div>
                  ) : (
                    <div>
                      <div className="fw-bold text-dark" style={{ fontSize: "13px" }}>Choose Excel (.xlsx / .xls)</div>
                      <small className="text-muted">Drag & drop or click</small>
                    </div>
                  )}
                </div>
              </div>

              {/* Multiple Images / ZIP Input */}
              <div className="mb-4">
                <label className="form-label fw-bold text-secondary mb-2">Step 2: Select Photo files in Bulk</label>
                <div 
                  className="p-3 border border-2 border-dashed rounded-3 text-center cursor-pointer hover-bg-light transition-all position-relative"
                  style={{ borderColor: Object.keys(selectedImages).length > 0 ? "#1976d2" : "#cbd5e1" }}
                >
                  <input
                    type="file"
                    ref={imageInputRef}
                    multiple
                    accept="image/*,.zip"
                    className="position-absolute w-100 h-100 top-0 start-0 opacity-0 cursor-pointer"
                    onChange={handleImageSelection}
                    disabled={uploading}
                  />
                  <FaImages size={30} className={Object.keys(selectedImages).length > 0 ? "text-primary mb-2" : "text-muted mb-2"} />
                  {Object.keys(selectedImages).length > 0 ? (
                    <div>
                      <div className="fw-bold text-primary">{Object.keys(selectedImages).length} Images Select Pool</div>
                      <small className="text-muted">Click or select more to append</small>
                    </div>
                  ) : (
                    <div>
                      <div className="fw-bold text-dark" style={{ fontSize: "13px" }}>Select gallery images or a ZIP archive</div>
                      <small className="text-muted">Select multiple JPG, PNG images or a single ZIP file</small>
                    </div>
                  )}
                </div>
              </div>

              {/* Action buttons */}
              <div className="d-flex gap-2 pt-2 border-top">
                <button
                  type="button"
                  className="btn btn-danger flex-grow-1 py-2 fw-bold d-flex align-items-center justify-content-center gap-2"
                  onClick={clearBulkImport}
                  disabled={uploading || (!excelFile && Object.keys(selectedImages).length === 0)}
                >
                  <AiOutlineClear size={16} /> Reset
                </button>
                <button
                  type="button"
                  className="btn text-white flex-grow-2 py-2 fw-bold d-flex align-items-center justify-content-center gap-2"
                  style={{ backgroundColor: "#ff671f" }}
                  onClick={startBulkUpload}
                  disabled={uploading || excelData.length === 0 || excelData.filter(row => row.errors.length === 0).length === 0}
                >
                  {uploading ? (
                    <>
                      <div className="spinner-border spinner-border-sm" role="status"></div> Uploading...
                    </>
                  ) : (
                    <>
                      <AiOutlinePlayCircle size={18} /> Run Bulk Upload
                    </>
                  )}
                </button>
              </div>

            </div>
          </div>

          {/* Import Grid and Progress Console */}
          <div className="col-lg-8">
            
            {/* Realtime Upload Progress Bar & Console */}
            {(uploading || uploadLogs.length > 0) && (
              <div className="card border-0 shadow-sm p-4" style={{ borderRadius: "12px" }}>
                <h5 className="fw-bold mb-3 text-dark d-flex align-items-center justify-content-between">
                  <span>🚀 Bulk Upload Execution Console</span>
                  {uploading && (
                    <span className="badge bg-warning text-dark animate-pulse">In Progress...</span>
                  )}
                </h5>

                {/* Progress Indicators */}
                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                    <div className="p-3 bg-light border rounded-3 text-center">
                      <span className="d-block fw-bold text-success fs-3">{successCount}</span>
                      <small className="text-muted fw-bold">Success Uploads</small>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="p-3 bg-light border rounded-3 text-center">
                      <span className="d-block fw-bold text-danger fs-3">{failCount}</span>
                      <small className="text-muted fw-bold">Failed Uploads</small>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="p-3 bg-light border rounded-3 text-center">
                      <span className="d-block fw-bold text-secondary fs-3">
                        {excelData.filter(row => {
                          if (row.errors.length > 0) return false;
                          if (row.type === "photo" && !isImageMatched(row.imageName)) return false;
                          return true;
                        }).length}
                      </span>
                      <small className="text-muted fw-bold font-sm">Queue Count</small>
                    </div>
                  </div>
                </div>

                {/* Log Terminal Window */}
                <div 
                  className="p-3 bg-dark text-white rounded-3 shadow-inner"
                  style={{ 
                    height: "180px", 
                    overflowY: "auto", 
                    fontFamily: "monospace", 
                    fontSize: "12px", 
                    lineHeight: "1.6" 
                  }}
                >
                  {uploadLogs.map((log, idx) => (
                    <div 
                      key={idx} 
                      className={`mb-1 ${
                        log.type === "success" ? "text-success" : 
                        log.type === "error" ? "text-danger" : 
                        "text-info"
                      }`}
                    >
                      <span className="text-muted font-sm me-2">[{log.time}]</span>
                      {log.message}
                    </div>
                  ))}
                  {uploadLogs.length === 0 && <div className="text-muted">Console standing by... Waiting to initialize upload process.</div>}
                  <div ref={consoleEndRef}></div>
                </div>
              </div>
            )}

            {/* Excel Row preview Table */}
            <div className="card border-0 shadow-sm p-4" style={{ borderRadius: "12px" }}>
              <h5 className="fw-bold mb-3 text-dark">Excel Rows Parsing Preview</h5>
              <p className="text-muted mb-4" style={{ fontSize: "13px" }}>
                Below is the preview of items loaded from your spreadsheet. Green items are valid and mapped. Red items are missing images or have invalid fields.
              </p>

              {excelData.length === 0 ? (
                <div className="text-center py-5 border rounded-3 text-muted">
                  <FaFileExcel size={36} className="mb-2 opacity-50" />
                  <h6>No Sheet Data Loaded Yet</h6>
                  <p className="mb-0 fs-7">Please choose a valid Excel spreadsheet to import gallery items.</p>
                </div>
              ) : (
                <div className="table-responsive" style={{ maxHeight: "400px", overflowY: "auto" }}>
                  <table className="table table-hover border align-middle table-sm" style={{ fontSize: "13px" }}>
                    <thead className="table-light sticky-top">
                      <tr>
                        <th className="text-center" style={{ width: "50px" }}>Row</th>
                        <th>Title</th>
                        <th>Category / Subcategory</th>
                        <th>Source File / Link</th>
                        <th>Order</th>
                        <th>Import Status</th>
                        <th className="text-center" style={{ width: "50px" }}>Cancel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {excelData.map((row) => {
                        const hasErrors = row.errors.length > 0;
                        const isPhoto = row.type === "photo";
                        const imageLoaded = isPhoto && isImageMatched(row.imageName);
                        
                        // Status badge display
                        let statusBadge;
                        if (row.status === "success") {
                          statusBadge = <span className="badge bg-success-subtle text-success border border-success border-opacity-25 py-1.5 px-2">Uploaded</span>;
                        } else if (row.status === "failed") {
                          statusBadge = <span className="badge bg-danger-subtle text-danger border border-danger border-opacity-25 py-1.5 px-2" title={row.errorMessage}>Upload Error</span>;
                        } else if (row.status === "uploading") {
                          statusBadge = <span className="badge bg-warning-subtle text-warning border border-warning border-opacity-25 py-1.5 px-2 animate-pulse">Uploading...</span>;
                        } else if (hasErrors) {
                          statusBadge = <span className="badge bg-danger text-white py-1.5 px-2" title={row.errors.join(", ")}>Form Error</span>;
                        } else if (isPhoto && !imageLoaded) {
                          statusBadge = <span className="badge bg-warning text-dark py-1.5 px-2">Missing Image</span>;
                        } else {
                          statusBadge = <span className="badge bg-info-subtle text-info border border-info border-opacity-25 py-1.5 px-2">Ready</span>;
                        }

                        return (
                          <tr 
                            key={row.id} 
                            className={
                              row.status === "success" ? "table-success-subtle" : 
                              row.status === "failed" ? "table-danger-subtle" :
                              hasErrors ? "table-danger-light" : ""
                            }
                          >
                            <td className="text-center fw-bold text-muted">{row.id}</td>
                            <td>
                              <div className="fw-bold">{row.title}</div>
                              {row.errors.map((err, i) => (
                                <div key={i} className="text-danger fw-semibold" style={{ fontSize: "10px" }}>⚠ {err}</div>
                              ))}
                            </td>
                            <td>
                              <div className="fw-semibold text-dark" style={{ fontSize: "12px" }}>{row.category || "N/A"}</div>
                              <div className="text-muted" style={{ fontSize: "10px" }}>{row.subcategory || "N/A"}</div>
                            </td>
                            <td>
                              {isPhoto ? (
                                <div>
                                  <span className="font-monospace text-dark d-block text-truncate" style={{ maxWidth: "150px" }} title={row.imageName}>
                                    📄 {row.imageName}
                                  </span>
                                  {imageLoaded ? (
                                    <small className="text-success fw-bold d-block" style={{ fontSize: "10px" }}>✓ Image File Mapped</small>
                                  ) : (
                                    <small className="text-danger fw-bold d-block" style={{ fontSize: "10px" }}>✗ File not selected</small>
                                  )}
                                </div>
                              ) : (
                                <div>
                                  <span className="font-monospace text-primary text-truncate d-block" style={{ maxWidth: "150px" }} title={row.videoUrl}>
                                    🔗 {row.videoUrl}
                                  </span>
                                  <small className="badge bg-danger-subtle text-danger" style={{ fontSize: "9px" }}>VIDEO URL</small>
                                </div>
                              )}
                            </td>
                            <td className="text-center font-monospace">{row.sequence}</td>
                            <td>{statusBadge}</td>
                            <td className="text-center">
                              <button 
                                className="btn btn-link btn-sm text-danger p-0"
                                onClick={() => removeRowFromImport(row.id)}
                                disabled={uploading}
                              >
                                <FaTimes />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>

        </div>
      )}

      {/* SINGLE UPLOAD MODAL */}
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">
            {isEditMode ? "✏️ Edit Gallery Item" : "🖼️ Add New Gallery Item"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={formik.handleSubmit}>
            <div className="mb-3">
              <label className="form-label fw-bold">Item Display Type *</label>
              <select
                name="type"
                className="form-select"
                value={formik.values.type}
                onChange={(e) => {
                  formik.setFieldValue("type", e.target.value);
                }}
              >
                <option value="photo">🖼️ Photo Image</option>
                <option value="video">🎥 YouTube Video Link</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">Select Category *</label>
              <select
                name="category"
                className="form-select"
                value={formik.values.category}
                onChange={(e) => {
                  const selectedCategory = e.target.value;
                  formik.setFieldValue("category", selectedCategory);
                  const subCats = CATEGORIES[selectedCategory] || [];
                  formik.setFieldValue("subcategory", subCats[0] || "");
                }}
              >
                {Object.keys(CATEGORIES).map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">Select Subcategory *</label>
              <select
                name="subcategory"
                className="form-select"
                value={formik.values.subcategory}
                onChange={formik.handleChange}
              >
                {(CATEGORIES[formik.values.category] || []).map((subcat) => (
                  <option key={subcat} value={subcat}>
                    {subcat}
                  </option>
                ))}
              </select>
            </div>

            <TextField
              name="title"
              fullWidth
              margin="dense"
              label="Item Caption / Title"
              value={formik.values.title}
              onChange={formik.handleChange}
            />
            {formik.errors.title && (
              <p className="text-danger mt-1" style={{ fontSize: "12px" }}>{formik.errors.title}</p>
            )}

            {/* Photo Type Upload */}
            {formik.values.type === "photo" && (
              <div className="mt-3">
                {isEditMode && selectedData && selectedData.image && (
                  <div className="mb-3 text-center">
                    <label className="form-label fw-bold text-start d-block">Current Gallery Photo</label>
                    <img
                      src={selectedData.image}
                      alt="Current"
                      className="rounded border shadow-sm img-thumbnail"
                      style={{ maxHeight: "140px", maxWidth: "100%", objectFit: "contain" }}
                    />
                  </div>
                )}
                <label className="form-label fw-bold">
                  {isEditMode ? "Replace Image File" : "Upload Image *"}
                </label>
                <input
                  type="file"
                  name="image"
                  accept=".jpg, .jpeg, .png"
                  className="form-control mt-1"
                  onChange={(event) => {
                    formik.setFieldValue("image", event.currentTarget.files[0]);
                  }}
                />
              </div>
            )}

            {/* Video Type Input fields */}
            {formik.values.type === "video" && (
              <div className="mt-3">
                <TextField
                  name="video_url"
                  fullWidth
                  margin="dense"
                  label="YouTube Video Link URL *"
                  value={formik.values.video_url}
                  onChange={formik.handleChange}
                />
                <TextField
                  name="description"
                  fullWidth
                  multiline
                  rows={2}
                  margin="dense"
                  label="Short Video Description / Caption (Optional)"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                />
              </div>
            )}

            <TextField
              name="sequence"
              fullWidth
              margin="dense"
              label="Display Priority Sequence (Order)"
              type="number"
              value={formik.values.sequence}
              onChange={formik.handleChange}
              inputProps={{ min: 0 }}
            />

            <div className="mt-4 pt-3 border-top text-end">
              <Button onClick={handleClose} color="inherit" className="me-2 fw-bold">Cancel</Button>
              <Button type="submit" variant="contained" style={{ backgroundColor: "#ff671f" }} className="fw-bold">
                {isEditMode ? "Save Changes" : "Create Item"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* Global Extra styles */}
      <style>{`
        .cursor-pointer { cursor: pointer; }
        .hover-bg-light:hover { background-color: #f1f5f9 !important; }
        .transition-all { transition: all 0.25s ease-in-out; }
        .table-success-subtle { background-color: rgba(46, 125, 50, 0.08) !important; }
        .table-danger-subtle { background-color: rgba(211, 47, 47, 0.08) !important; }
        .table-danger-light { background-color: rgba(211, 47, 47, 0.03) !important; }
        .flex-grow-2 { flex-grow: 2 !important; }
        .fs-7 { fontSize: 13px; }
        .shadow-inner { box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06); }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>

    </div>
  );
};

export default AddGallery;
